import React, { useRef } from 'react';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import Icon from '@mdi/react';
import { mdiAlertCircleOutline } from '@mdi/js';
// Debounce utility for promises
function debouncePromise(fn, delay) {
  let timeoutId;
  let lastReject;
  return (...args) =>
    new Promise((resolve, reject) => {
      if (timeoutId) clearTimeout(timeoutId);
      if (lastReject) lastReject();
      lastReject = reject;
      timeoutId = setTimeout(() => {
        fn(...args)
          .then(resolve)
          .catch(reject);
      }, delay);
    });
}

function ErrorIndicator(props) {
  return (
    <components.DropdownIndicator {...props}>
      <Icon path={mdiAlertCircleOutline} size={0.8} color="#F06548" />
    </components.DropdownIndicator>
  );
}

function BookingAsyncAutocomplete({
  autoCompleteFn,
  mappedFn,
  onChange,
  value,
  displayOption = 'label',
  formatOptionLabelCustom = null,
  inputIcon = null,
  rightKey = null,
  debounceDelay = 600,
  width,
  error = false,
  stylesOverride,
  ...props
}) {
  const menuPortalTarget =
    typeof document !== 'undefined' ? document.body : null;

  const formatOptionLabel = (option, { context }) => {
    // when rendering the “selected” value in the input:
    if (context === 'value') {
      let displayLabel = '';
      if (option?.label) {
        if (option.label.length > 20) {
          displayLabel = `${option.label.slice(0, 20)}...`;
        } else {
          displayLabel = option.label;
        }
      }

      return (
        <div className="d-flex align-items-center gap-1">
          <div className="d-flex align-items-center gap-1">
            {inputIcon && inputIcon}
            <span>{displayLabel}</span>
          </div>

          <span
            style={{
              color: option.color || '#475467',
            }}
          >
            {rightKey && '#'}
            {rightKey ? option?.[rightKey] || option.value : null}
          </span>
        </div>
      );
    }
    // when rendering the menu list:
    if (displayOption === 'value') return option.value;
    if (displayOption === 'both') return `${option.label} | ${option.value}`;
    if (displayOption === 'bothCustom') {
      return (
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center', // keeps your other items centered
          }}
        >
          <div
            style={{
              width: '70%',
            }}
          >
            <span>{option.label}</span>
          </div>
          <span
            style={{
              width: '1px',
              backgroundColor: option.color || '#ccc',
              alignSelf: 'stretch',
            }}
          />
          <span style={{ opacity: 0.6, marginLeft: '10px' }}>
            {rightKey ? option?.[rightKey] : option.value}
          </span>
        </div>
      );
    }
    /* else */ return option.label;
  };

  const selectStyles = () => ({
    control: base => ({
      ...base,
      border: 'none',
      boxShadow: 'none',
      backgroundColor: 'transparent',
      minWidth: '160px',
      width: width || '250px',
      maxwidth: width || '250px',
      minHeight: 'auto',
      '&:hover': {
        border: 'none',
      },
    }),
    valueContainer: base => ({
      ...base,
      paddingRight: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: width || '250px',
      cursor: 'text',
    }),
    singleValue: base => ({
      ...base,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: width,
    }),
    input: base => ({
      ...base,
      paddingRight: '0',
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 9999,
    }),
    menu: base => ({
      ...base,
      width,
    }),
    menuList: base => ({
      ...base,
      width,
    }),
    '&:focus-within': {
      boxShadow: 'none',
    },
    indicatorsContainer: () => null,
    dropdownIndicator: () => null,
    indicatorSeparator: () => null,
  });

  const renderTitle = () => {
    if (value) {
      return value.label;
    }
    return props.placeholder || 'Search...';
  };

  // Debounced loadOptions
  const debouncedLoadOptions = useRef(
    debouncePromise(async inputValue => {
      if (!inputValue) return [];
      const data = await autoCompleteFn(inputValue);
      return mappedFn(data);
    }, debounceDelay),
  ).current;

  return (
    <span title={renderTitle()}>
      <AsyncSelect
        cacheOptions={false}
        defaultOptions
        loadOptions={debouncedLoadOptions}
        onChange={onChange}
        className={`basic-single ${error ? 'is-invalid' : ''}`}
        classNamePrefix="select"
        isClearable={false}
        isSearchable
        placeholder={props.placeholder || 'Search...'}
        value={value}
        menuPortalTarget={menuPortalTarget}
        styles={selectStyles()}
        formatOptionLabel={formatOptionLabelCustom ?? formatOptionLabel}
        isDisabled={props.isDisabled ?? false}
        components={{
          DropdownIndicator: error ? ErrorIndicator : null,
          IndicatorSeparator: null,
          LoadingMessage: () => null,
          LoadingIndicator: () => null,
        }}
        noOptionsMessage={() => null}
        name={props?.name}
        inputId={`${props?.name}-input`}
      />
    </span>
  );
}
export default BookingAsyncAutocomplete;
